import React, { useState, useEffect, useCallback } from "react";
import { Space, Row, Divider, Button, Checkbox, Form, Input, Radio, RadioChangeEvent } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import { useAsyncAxios, utilAxiosWithAppToken } from "../utils/customAxios";
import { useNavigate } from "react-router-dom";
import { AddressType } from "../components/AddressMaker";
import { useAlert, AlertType } from "../provider/AlertProvider";

import * as TypeDTO from "../commons/typeDTO";
import * as Request from "../commons/request";
import * as String from "../commons/string";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";

import PageContainer from "../layout/PageContainer";
import styles from "./Login.module.css";
import PhoneCertification from "../components/PhoneCertification";
import InputPassword from "../components/InputPassword";

function SignUp() {
    const navigate = useNavigate();
    const alert = useAlert();
    const [form] = Form.useForm();

    const [agreeAllTerms, setAgreeAllTerms] = useState(false);
    const [agreeTosTerms, setAgreeTosTerms] = useState(false);
    const [agreePrivacPolicyTerms, setAgreePrivacPolicyTerms] = useState(false);

    const [member, setMember] = useState("INDIVIDUAL");
    const [userName, setUserName] = useState("");
    const [certificationResult, setCertificationResult] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [userAddress, setUserAddress] = useState<AddressType>();
    const [disableInputName, setDisableInputName] = useState(false);
    const [email, setEmail] = useState("");

    useEffect(() => {
        if (email.includes(" ")) {
            const newEmail = email.replace(/\s/g, "");
            form.setFieldsValue({
                email: newEmail,
            });
            setEmail(newEmail);
        }
    }, [email]);

    const handleToggleAllTerm = (checked: boolean) => {
        setAgreeAllTerms(checked);

        if (checked) {
            setAgreeTosTerms(true);
            setAgreePrivacPolicyTerms(true);
        } else {
            setAgreeTosTerms(false);
            setAgreePrivacPolicyTerms(false);
        }
    };

    const handleToggleTosTerm = (checked: boolean) => {
        setAgreeTosTerms(checked);

        if (checked && agreePrivacPolicyTerms) {
            setAgreeAllTerms(true);
        } else {
            setAgreeAllTerms(false);
        }
    };

    const handleTogglePrivacPolicyTerm = (checked: boolean) => {
        setAgreePrivacPolicyTerms(checked);

        if (checked && agreeTosTerms) {
            setAgreeAllTerms(true);
        } else {
            setAgreeAllTerms(false);
        }
    };

    const requestAxiosAddUser = async (data: TypeDTO.SignupDto) => {
        const response = await utilAxiosWithAppToken().post(Request.SIGNUP_URL, data);

        return response.data;
    };

    const { loading: loadingAddUser, error: errorAddUser, data: resultAddUser, execute: requestAddUser } = useAsyncAxios(requestAxiosAddUser);

    useEffect(() => {
        if (!resultAddUser) return;

        navigate(Common.PAGE_LOGIN, { replace: true });
        alert.setAlert(AlertType.SUCCESS, "회원가입 성공", "회원가입을 완료하였습니다.");
    }, [resultAddUser]);

    useEffect(() => {
        if (!errorAddUser) return;

        console.log("errorUser", errorAddUser);
        alert.setAlert(errorAddUser.response?.data?.code, "회원가입 실패", errorAddUser.response?.data?.message);
    }, [errorAddUser]);

    const checkIndividualType = () => {
        if (member === "INDIVIDUAL") return true;
        return false;
    };

    const validatePwd = useCallback((_: any, value: string) => {
        const pwd_regExp = Common.PASSWORD_REGEXP;

        if (value && !pwd_regExp.test(value)) {
            return Promise.reject(new Error(String.msg_pwd_rule));
        }
        return Promise.resolve();
    }, []);

    const validateCompanyRegistrationNo = useCallback((_: any, value: string) => {
        const valueMap = value
            .replace(/-/gi, "")
            .split("")
            .map((item) => {
                return parseInt(item, 10);
            });

        if (valueMap.length !== 10) {
            return Promise.reject(new Error(String.msg_company_registration_no_rule));
        }
        const multiply = new Array<number>(1, 3, 7, 1, 3, 7, 1, 3, 5);
        let checksum = 0;

        for (let i = 0; i < multiply.length; ++i) {
            checksum += multiply[i] * valueMap[i];
        }
        checksum += Math.floor((multiply[8] * valueMap[8]) / 10);

        if (Math.floor(valueMap[9]) === (10 - (checksum % 10)) % 10) {
            return Promise.resolve();
        } else {
            return Promise.reject(new Error(String.msg_company_registration_no_rule));
        }
    }, []);

    const handleCertificationResult = (success: boolean, phoneNumber: string) => {
        setPhoneNumber(phoneNumber);
        setCertificationResult(success);

        setDisableInputName(success);
    };

    const onChangedMemberType = (e: RadioChangeEvent) => {
        setMember(e.target.value);
    };

    const onFinish = (values: any) => {
        delete values["confirm"];
        const data: TypeDTO.SignupDto = {
            companyName: "",
            ...values,
            phone: phoneNumber,
            address: userAddress?.address,
            addressDetails: userAddress?.addressDetails,
        };
        console.log("Received values of form: ", data);
        requestAddUser(data);
    };

    return (
        <PageContainer pageBackground={`#fff`} contentPadding={16}>
            <Row align="middle" justify="center">
                <div className={`${styles.loginCard} container`}>
                    <p className={`${styles.description}`}>
                        통합 회원 가입으로 RUTA40의 모든 서비스를
                        <br /> 한 번에 이용하실수 있습니다.
                    </p>
                    <Divider />
                    <Form layout="vertical" form={form} onFinish={onFinish}>
                        <Form.Item className={styles.textInputLabel} required label="계정 타입" name="userType" initialValue={member}>
                            <Radio.Group onChange={onChangedMemberType}>
                                <Radio className={styles.radioButton} value="INDIVIDUAL">
                                    개인 회원
                                </Radio>
                                <Radio className={styles.radioButton} value="CORPORATE">
                                    법인 회원
                                </Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            className={styles.textInputLabel}
                            name="name"
                            label={checkIndividualType() ? "이름" : "이름(대표자)"}
                            initialValue={userName}
                            rules={[
                                {
                                    required: true,
                                    message: "이름을 입력해 주세요.",
                                },
                                { max: 50, message: String.msg_name_max },
                            ]}
                        >
                            <Input
                                className={styles.loginInput}
                                placeholder="휴대전화 명의자 이름을 입력해 주세요."
                                disabled={disableInputName}
                                onChange={(e) => setUserName(e.target.value)}
                            />
                        </Form.Item>

                        {checkIndividualType() === false ? (
                            <Form.Item
                                className={styles.textInputLabel}
                                rules={[
                                    {
                                        required: true,
                                        message: "회사명을 입력해 주세요.",
                                    },
                                ]}
                                name="companyName"
                                label="회사명"
                            >
                                <Input className={styles.loginInput} placeholder="회사명" />
                            </Form.Item>
                        ) : null}

                        {checkIndividualType() === false ? (
                            <Form.Item
                                className={styles.textInputLabel}
                                name="companyRegistrationNo"
                                label="사업자 등록번호"
                                rules={[
                                    {
                                        required: true,
                                        message: String.msg_company_registration_no_must,
                                    },
                                    {
                                        pattern: new RegExp(/^([-]?[1-9][0-9]*|0)$/),
                                        message: String.msg_company_registration_no_only_number,
                                    },
                                    {
                                        validator: validateCompanyRegistrationNo,
                                    },
                                ]}
                            >
                                <Input className={styles.loginInput} placeholder="사업자 등록번호(- 미포함)" />
                            </Form.Item>
                        ) : null}

                        <Form.Item
                            className={styles.textInputLabel}
                            name="email"
                            label={String.email}
                            rules={[
                                {
                                    required: true,
                                    message: "이메일을 입력해 주세요.",
                                },
                                {
                                    validator: (_, value) => Utils.validateEmail(true, true, value),
                                },
                            ]}
                        >
                            <Input
                                className={styles.loginInput}
                                placeholder={String.email}
                                value={email}
                                onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toLowerCase())}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </Form.Item>

                        <Form.Item
                            className={styles.textInputLabel}
                            name="password"
                            label={String.password}
                            rules={[
                                {
                                    required: true,
                                    message: String.msg_pwd_must,
                                },
                                { validator: validatePwd },
                            ]}
                            hasFeedback
                        >
                            <InputPassword />
                        </Form.Item>

                        <Form.Item
                            className={styles.textInputLabel}
                            name="confirm"
                            dependencies={["password"]}
                            hasFeedback
                            rules={[
                                {
                                    required: true,
                                    message: "비밀번호를 확인해 주세요.",
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue("password") === value) {
                                            return Promise.resolve();
                                        }

                                        return Promise.reject(new Error("입력한 비밀번호가 일치하지 않습니다."));
                                    },
                                }),
                            ]}
                        >
                            <InputPassword />
                        </Form.Item>

                        <Form.Item
                            className={styles.textInputLabel}
                            name="phone"
                            label={String.phone}
                            required={true}
                            rules={[
                                () => ({
                                    validator(_, value) {
                                        if (certificationResult) {
                                            return Promise.resolve();
                                        }

                                        return Promise.reject(new Error("휴대전화 본인 인증을 해 주세요."));
                                    },
                                }),
                            ]}
                        >
                            <PhoneCertification userName={userName} onChangedResult={handleCertificationResult} />
                        </Form.Item>
                        <Form.Item className={styles.loginBtnWrapper}>
                            <Button className={styles.loginBtn} block type="primary" htmlType="submit" size="large" disabled={!agreeAllTerms}>
                                {String.register}
                            </Button>
                        </Form.Item>
                    </Form>
                    <Space style={{ width: "100%", marginBottom: "88px" }} direction="vertical" size={20}>
                        <Checkbox checked={agreeAllTerms} onChange={(e) => handleToggleAllTerm(e.target.checked)}>
                            약관 전체 동의
                        </Checkbox>
                        <Row justify={"space-between"}>
                            <Checkbox checked={agreeTosTerms} onChange={(e) => handleToggleTosTerm(e.target.checked)}>
                                서비스 이용약관 동의 <span style={{ color: "var(--text-highlight)" }}>(필수)</span>
                            </Checkbox>
                            <Button size="small" type="text" icon={<CaretRightOutlined />} onClick={() => navigate(Common.PAGE_TOS)}></Button>
                        </Row>
                        <Row justify={"space-between"}>
                            <Checkbox checked={agreePrivacPolicyTerms} onChange={(e) => handleTogglePrivacPolicyTerm(e.target.checked)}>
                                개인정보 수집 및 이용 동의 <span style={{ color: "var(--text-highlight)" }}>(필수)</span>
                            </Checkbox>
                            <Button size="small" type="text" icon={<CaretRightOutlined />} onClick={() => navigate(Common.PAGE_PRIVACY)}></Button>
                        </Row>
                    </Space>
                </div>
            </Row>
        </PageContainer>
    );
}

export default SignUp;
